// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { useCustomers } from '@/customer/hooks/use-customers';
import { BaseSearchableFilter } from '@/filters/base-searchable-filter';
import { useMemo } from 'react';

interface Props {
  /**
   * An array of strings representing the currently selected customer IDs.
   */
  value: string[];

  /**
   * The label for the filter menu.
   */
  label?: string;

  /**
   * A callback fired when the selected customer IDs change.
   */
  onChange: (value: string[]) => void;

  /**
   * Whether to include the user's organization as an option.
   */
  includeOwnOrganization?: boolean;

  /**
   * Whether to show the search input. Defaults to true.
   */
  enableSearch?: boolean;
}

/**
 * A searchable filter for selecting zero or more customers from the remote API.
 */
export function CustomerFilter(props: Props) {
  const { customers, isLoading } = useCustomers();
  const { user } = useCurrentUser();

  // Memoized on the customers: the options no longer carry selection
  const options = useMemo(() => {
    const customerOptions = customers.map(({ id, name }) => ({
      value: id,
      label: name,
    }));

    return props.includeOwnOrganization
      ? [
          { value: 'ORGANIZATION', label: user.organization.name },
          ...customerOptions,
        ]
      : customerOptions;
  }, [customers, props.includeOwnOrganization, user.organization.name]);

  const handleChangeOption = (id: string, isActive: boolean) => {
    if (isActive) {
      return props.onChange([...props.value, id]);
    }

    props.onChange(props.value.filter((customerId) => customerId !== id));
  };

  return (
    <BaseSearchableFilter
      label={props.label ?? 'Customers'}
      options={options}
      selectedValues={props.value}
      onChangeOption={handleChangeOption}
      searchPlaceholder="Search customers..."
      enableSearch={props.enableSearch}
      isLoading={isLoading}
    />
  );
}
